import { useState } from "react";
import {
  Loader2,
  Pin,
  PinOff,
  Share2,
  Trash2,
  User as UserIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { cn } from "@/lib/utils";
import type { SavedViewRead } from "@/api/types";

interface ManageLayoutViewsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  views: SavedViewRead[];
  /** Operator can flip default + share on shared views — gated
   *  on settings.project.edit upstream. */
  canManageShared: boolean;
  onRename: (id: number, name: string) => Promise<unknown>;
  onDelete: (id: number) => Promise<unknown>;
  onToggleShare: (id: number, shared: boolean) => Promise<unknown>;
  onSetDefault: (id: number, on: boolean) => Promise<unknown>;
}

/**
 * Drawer-style dialog listing every saved layout view for the
 * project. Each row supports rename (inline), share toggle
 * (admin only, on shared views), set-as-default (admin only, on
 * shared views), and delete (own views or admin). Closing the
 * dialog discards no state — every action persists immediately.
 */
export function ManageLayoutViewsDialog({
  open,
  onOpenChange,
  views,
  canManageShared,
  onRename,
  onDelete,
  onToggleShare,
  onSetDefault,
}: ManageLayoutViewsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Manage layout views</DialogTitle>
          <DialogDescription>
            Rename, share, or delete saved layout views for this project.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[420px] space-y-2 overflow-y-auto pr-1">
          {views.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-400">
              No saved views yet.
            </div>
          ) : (
            views.map((view) => (
              <ManageRow
                key={view.id}
                view={view}
                canManageShared={canManageShared}
                onRename={onRename}
                onDelete={onDelete}
                onToggleShare={onToggleShare}
                onSetDefault={onSetDefault}
              />
            ))
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManageRow({
  view,
  canManageShared,
  onRename,
  onDelete,
  onToggleShare,
  onSetDefault,
}: Omit<ManageLayoutViewsDialogProps, "open" | "onOpenChange" | "views"> & {
  view: SavedViewRead;
}) {
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(view.name);
  const [busy, setBusy] = useState(false);
  // Operator can rename / delete their own view; admins can do
  // both on any shared view.
  const canEdit = view.is_mine || canManageShared;
  // Toggling default + share is reserved for admins, and only
  // makes sense on shared views (private views can't be set as
  // the project default).
  const canToggleDefault = canManageShared && view.is_shared;

  async function commitRename() {
    const trimmed = draftName.trim();
    if (!trimmed || trimmed === view.name) {
      setEditing(false);
      setDraftName(view.name);
      return;
    }
    setBusy(true);
    await onRename(view.id, trimmed);
    setBusy(false);
    setEditing(false);
  }

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-lg border bg-white px-3 py-2.5",
        view.is_default
          ? "border-amber-200 bg-amber-50/40"
          : "border-slate-200",
      )}
    >
      <ViewBadge view={view} />
      <div className="min-w-0 flex-1">
        {editing ? (
          <Input
            autoFocus
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={() => void commitRename()}
            onKeyDown={(e) => {
              if (e.key === "Enter") void commitRename();
              if (e.key === "Escape") {
                setEditing(false);
                setDraftName(view.name);
              }
            }}
            className="h-7 text-sm"
            disabled={busy}
          />
        ) : (
          <button
            type="button"
            disabled={!canEdit}
            onClick={() => setEditing(true)}
            className={cn(
              "block w-full truncate text-left text-sm font-medium text-slate-900",
              canEdit && "hover:text-indigo-600",
            )}
          >
            {view.name}
          </button>
        )}
        <div className="mt-0.5 text-[11px] text-slate-400">
          {view.is_shared ? "Shared" : "Private"} · by{" "}
          {view.user_name ?? "—"}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
        {canToggleDefault && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={async () => {
              setBusy(true);
              await onSetDefault(view.id, !view.is_default);
              setBusy(false);
            }}
            disabled={busy}
            className="h-7 px-2 text-xs"
          >
            {view.is_default ? (
              <>
                <PinOff className="mr-1 h-3 w-3" /> Unset
              </>
            ) : (
              <>
                <Pin className="mr-1 h-3 w-3" /> Set default
              </>
            )}
          </Button>
        )}
        {canManageShared && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={async () => {
              setBusy(true);
              await onToggleShare(view.id, !view.is_shared);
              setBusy(false);
            }}
            disabled={busy}
            className="h-7 px-2 text-xs"
            title={view.is_shared ? "Make private" : "Share with team"}
          >
            <Share2
              className={cn(
                "h-3.5 w-3.5",
                view.is_shared ? "text-indigo-500" : "text-slate-400",
              )}
            />
          </Button>
        )}
        {canEdit && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={async () => {
              const ok = await confirm({
                title: "Delete view",
                description: `Delete "${view.name}"? This cannot be undone.`,
                confirmLabel: "Delete",
                variant: "danger",
                icon: <Trash2 className="h-5 w-5" />,
              });
              if (!ok) return;
              setBusy(true);
              await onDelete(view.id);
              setBusy(false);
            }}
            disabled={busy}
            className="h-7 px-2 text-rose-500 hover:text-rose-700"
            title="Delete view"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

function ViewBadge({ view }: { view: SavedViewRead }) {
  if (view.is_default) {
    return <Pin className="h-4 w-4 shrink-0 text-amber-500" />;
  }
  if (view.is_shared) {
    return <Share2 className="h-4 w-4 shrink-0 text-indigo-500" />;
  }
  return <UserIcon className="h-4 w-4 shrink-0 text-slate-400" />;
}
